import { Fragment, useRef, useState, useEffect, useMemo } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { CheckIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { changeSlotOrderforAvailableAppointments } from "./DataServices/SlotOrderDataServices";

import {
  DeleteReminder,
  getReminderByAvailableAppointmentID,
} from "./DataServices/ReminderService";
import MessageBox from "./MessageBox";
import Loading from "./Loading";
import { IAvailableAppointment, IMainAppointment, Reminder } from "./Models";
import {
  DeleteAppointmentSlot,
  generateAppointmentsAndUpdateState,
  getEasternCurrentTime,
  getMainAppointment,
  resendemail,
  saveMainAppointment,
} from "./DataServices/AppointmentsDataServices";
import Notification from "./Notification";
import { TrashIcon } from "@heroicons/react/20/solid";

interface IDefaultSortOrderItem {
  id: string;
  name: string;
  slotOrder: number;
  startTime: Date | null;
  endTime: Date | null;
  slotCapacity: number;
  reminders: Reminder[];
}

interface IAddAppointmentProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  isVacant: boolean;
  listingId: string;
  appointmentToBeEdited: IAvailableAppointment;
  loadMainAppointment?: boolean;
}

export default function AddAppointment({
  open,
  setOpen,
  listingId,
  isVacant,
  appointmentToBeEdited,
  loadMainAppointment = true,
}: IAddAppointmentProps) {
  const cancelButtonRef = useRef(null);

  const emptyMainAppointment: IMainAppointment = useMemo(
    () => ({
      id: "",
      listingId: listingId,
      openingTime: getEasternCurrentTime(),
      closingTime: null,
      openHoursBefore: undefined,
      openHoursAfter: undefined,
      rosterTime: undefined,
      resendCancelled: false,
      resendUnconfirmed: false,
    }),
    [listingId],
  );

  const [selectedStartTime, setSelectedStartTime] = useState<
    string | number | Date | dayjs.Dayjs | null | undefined
  >(
    appointmentToBeEdited.startTime
      ? new Date(appointmentToBeEdited.startTime)
      : new Date(),
  );

  const [selectedEndTime, setSelectedEndTime] = useState<
    string | number | Date | dayjs.Dayjs | null | undefined
  >(
    appointmentToBeEdited.endTime
      ? new Date(appointmentToBeEdited.endTime)
      : new Date(),
  );

  const [selectedCapacity, setSelectedCapacity] = useState(
    appointmentToBeEdited.capacity ? appointmentToBeEdited.capacity : 5,
  );
  const [openNotification, setOpenNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationMessageType, setNotificationMessageType] =
    useState("success");
  const [selectedSlot, setSelectedSlot] = useState(60);
  const [days, setDays] = useState<number | undefined>(undefined);
  const [mainAppointment, setMainAppointment] =
    useState<IMainAppointment>(emptyMainAppointment);
  const [hours, setHours] = useState<number | undefined>(undefined);
  const [minutes, setMinutes] = useState<number | undefined>(undefined);
  const [loadOnChangeReminder, setLoadOnChangeReminder] = useState(false);
  const [isConfigureByDate, setIsConfigureByDate] = useState(true);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirmDeleteReminder, setConfirmDeleteReminder] =
    useState<boolean>(false);
  const [reminderToDelete, setReminderToDelete] = useState<Reminder | null>(
    null,
  );
  const [reminderType, setReminderType] = useState("Reminder");

  const [applicationReminderType, setApplicationReminderType] =
    useState("Before");
  const [numberOfDays, setNumberOfDays] = useState(0);

  const isChecked = applicationReminderType === "After";

  useEffect(() => {
    if (
      open &&
      appointmentToBeEdited?.id === "" &&
      reminders.length === 0 // Only set if no reminders currently
    ) {
      const reminderSets: { [key: string]: number[] } = {
        Reminder: [180],
        "Reminder for Details": [30],
      };

      const allReminders = Object.entries(reminderSets).flatMap(
        ([type, times]) =>
          times.map((time) => ({
            id: "",
            availableAppointmentId: "",
            reminder: time,
            reminderType: type,
          })),
      );

      setReminders(allReminders);
    }
  }, [open, reminderType, reminders]);

  useEffect(() => {
    if (isVacant) {
      setSelectedSlot(60);
    } else {
      setSelectedSlot(10);
    }
  }, [isVacant]);

  useEffect(() => {
    if (selectedEndTime != null) {
      let closingTime = dayjs(selectedEndTime).toDate();

      if (numberOfDays > 0) {
        closingTime = dayjs(selectedEndTime).add(numberOfDays, "day").toDate();
      }

      setMainAppointment(
        (prev: IMainAppointment): IMainAppointment => ({
          ...prev,
          closingTime: closingTime,
        }),
      );
    }
  }, [selectedEndTime, numberOfDays]);

  useEffect(() => {
    setSelectedStartTime(
      appointmentToBeEdited.startTime
        ? new Date(appointmentToBeEdited.startTime)
        : null,
    );
    setSelectedEndTime(
      appointmentToBeEdited.endTime
        ? new Date(appointmentToBeEdited.endTime)
        : null,
    );
    setSelectedCapacity(appointmentToBeEdited.capacity);

    if (appointmentToBeEdited.id.length > 0) {
      getReminderByAvailableAppointmentID(appointmentToBeEdited.id)
        .then((data) => {
          setReminders(data);
        })
        .catch(() => {
          console.log("Error fetching reminders");
        });

      if (loadMainAppointment && appointmentToBeEdited.groupId) {
        getMainAppointment(appointmentToBeEdited.groupId)
          .then((data) => {
            if (!data) {
              setMainAppointment(emptyMainAppointment);
              setIsConfigureByDate(false);
              return;
            }

            const time =
              data.openingTime != "0001-01-01T00:00:00" &&
              data.closingTime != "0001-01-01T00:00:00";
            setMainAppointment({
              ...data,
              openingTime: time ? new Date(data.openingTime) : null,
              closingTime: time ? new Date(data.closingTime) : null,
            });
            setIsConfigureByDate(time);
          })
          .catch(() => {
            setMainAppointment(emptyMainAppointment);
            setIsConfigureByDate(false);
          });
      } else if (!loadMainAppointment) {
        setMainAppointment(emptyMainAppointment);
        setIsConfigureByDate(false);
      }
    }
  }, [appointmentToBeEdited, open, loadMainAppointment, emptyMainAppointment]);

  const handleCapacityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newCapacity = parseInt(event.target.value, 10);
    if (newCapacity < 0) {
      setSelectedCapacity(0);
    } else {
      setSelectedCapacity(newCapacity);
    }
  };

  const handleSlotChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSlot = parseInt(event.target.value);
    setSelectedSlot(newSlot);
  };

  const DeleteAppointment = async () => {
    setLoading(true);

    try {
      await DeleteAppointmentSlot(appointmentToBeEdited.id);
      setOpen(false);
      setLoading(false);
      setReminders([]);
      setMainAppointment(emptyMainAppointment);
      setNotificationMessage("Appointment Deleted Successfully!");
      setNotificationMessageType("success");
      setOpenNotification(true);
      setTimeout(() => {
        setOpenNotification(false);
      }, 3000);
    } catch (error) {
      setLoading(false);
      setNotificationMessage("Failed to delete Appointment!");
      setNotificationMessageType("error");
      setOpenNotification(true);
      setTimeout(() => {
        setOpenNotification(false);
      }, 3000);
      console.error("Error:", error);
    }
  };

  const handleOnSave = async () => {
    setLoading(true);

    try {
      const hasTimeRange = selectedStartTime && selectedEndTime;
      const hasValidHours =
        (mainAppointment.openingTime && mainAppointment.closingTime) ||
        mainAppointment.openHoursAfter ||
        mainAppointment.openHoursBefore;

      if (!hasTimeRange || !hasValidHours) {
        alert("Some value is missing or invalid!");
        setLoading(false);
        return;
      }

      const newStartTime = dayjs(selectedStartTime).toDate();
      const newEndTime = dayjs(selectedEndTime).toDate();

      if (newStartTime >= newEndTime) {
        alert("Invalid time range");
        setLoading(false);
        return;
      }

      const minTime = dayjs("0001-01-01T00:00:00");
      const isInvalidTime = (time: dayjs.Dayjs) =>
        !time.isValid() || time.isSame(minTime, "second");

      const updatedMainAppointment = { ...mainAppointment };

      if (isConfigureByDate) {
        if (
          isInvalidTime(dayjs(updatedMainAppointment.openingTime)) ||
          isInvalidTime(dayjs(updatedMainAppointment.closingTime))
        ) {
          alert("Invalid opening or closing time!");
          setLoading(false);
          return;
        }
        updatedMainAppointment.openHoursAfter = 0;
        updatedMainAppointment.openHoursBefore = 0;
      } else {
        updatedMainAppointment.openingTime = null;
        updatedMainAppointment.closingTime = null;
      }

      if (
        !updatedMainAppointment.id ||
        updatedMainAppointment.id.length === 0
      ) {
        updatedMainAppointment.id = uuidv4();
      }

      await saveMainAppointment(updatedMainAppointment);

      const _selectedCapacity = !isVacant ? selectedCapacity : 500;
      let _selectedSlot = !isVacant ? selectedSlot : 60;
      _selectedSlot =
        _selectedSlot === 0 || _selectedSlot == null ? 10 : _selectedSlot;

      if (!appointmentToBeEdited.id || appointmentToBeEdited.id.length === 0) {
        await generateAppointmentsAndUpdateState(
          listingId,
          newStartTime,
          newEndTime,
          _selectedSlot,
          _selectedCapacity,
          reminders,
          updatedMainAppointment.id,
        );
        if (numberOfDays > 0) {
          for (let i = 1; i <= numberOfDays; i++) {
            const start = dayjs(selectedStartTime).add(i, "day").toDate();
            const end = dayjs(selectedEndTime).add(i, "day").toDate();
            await generateAppointmentsAndUpdateState(
              listingId,
              start,
              end,
              _selectedSlot,
              _selectedCapacity,
              reminders,
              updatedMainAppointment.id,
            );
          }
        }
      }

      const appointmentItem: IDefaultSortOrderItem = {
        id: appointmentToBeEdited.id,
        slotOrder: _selectedSlot,
        startTime: newStartTime,
        endTime: newEndTime,
        name: "",
        slotCapacity: _selectedCapacity,
        reminders,
      };

      await changeSlotOrderforAvailableAppointments([appointmentItem]);

      // Resetting states after successful save
      setOpen(false);
      setLoading(false);
      setReminders([]);
      setMainAppointment(emptyMainAppointment);

      setNotificationMessage(
        appointmentToBeEdited.id && appointmentToBeEdited.id.length !== 0
          ? "Appointment Edited Successfully!"
          : "Appointment Added Successfully!",
      );
      setNotificationMessageType("success");
      setOpenNotification(true);
      setNumberOfDays(0);
      setTimeout(() => {
        setOpenNotification(false);
      }, 3000);
    } catch (error) {
      setLoading(false);
      setNotificationMessage("Failed to add Appointment!");
      setNotificationMessageType("error");
      setOpenNotification(true);
      setTimeout(() => {
        setOpenNotification(false);
      }, 3000);
      console.error("Error:", error);
    }
  };

  const handleAddReminder = async () => {
    try {
      // Validate that at least one of the time fields is selected
      if (!days && !hours && !minutes) {
        alert("Please select a reminder time");
        return;
      }

      const dd = days ?? 0;
      const hrs = hours ?? 0;
      const mins = minutes ?? 0;

      if (mins >= 60 || hrs >= 24) {
        alert(
          "Invalid reminder time: Hours should be less than 24, and minutes less than 60",
        );
        return;
      }

      const totalTimeInMinutes = dd * 24 * 60 + hrs * 60 + mins;

      const finalReminderType =
        reminderType === "Application Reminder"
          ? `Application Reminder ${applicationReminderType}`
          : reminderType;

      const reminder: Reminder = {
        id: "",
        availableAppointmentId: appointmentToBeEdited
          ? appointmentToBeEdited.id
          : "",
        reminder: totalTimeInMinutes,
        reminderType: finalReminderType,
      };

      setReminders([...reminders, reminder]);
      setDays(undefined);
      setHours(undefined);
      setMinutes(undefined);
    } catch (error) {
      console.error(`Error on add reminder: ${error}`);
    }
  };

  const handleDeleteReminder = async (reminder: Reminder) => {
    setConfirmDeleteReminder(true);
    setReminderToDelete(reminder);
  };

  const handleDeleteReminderOnConfirm = async () => {
    if (reminderToDelete == null) {
      return;
    }
    if (reminderToDelete.id.length > 0)
      await DeleteReminder(reminderToDelete.id);
    var newReminders = reminders.filter(
      (e) => e.reminder !== reminderToDelete.reminder,
    );
    setReminders(newReminders);
    setLoadOnChangeReminder(!loadOnChangeReminder);
    setReminderToDelete(null);
    setConfirmDeleteReminder(false);
  };

  const DislayReminder = (e: number) => {
    var day = Math.floor(e / (24 * 60));
    e = e % (24 * 60);
    var hour = Math.floor(e / 60);
    var minute = e % 60;

    return `${day} Days ${hour} Hrs ${minute} Mins`;
  };

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          initialFocus={cancelButtonRef}
          onClose={setOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden w-[90%] rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:p-6">
                  {/* Main Heading */}
                  <div className="flex items-center justify-center gap-2 mt-2 mb-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                      <CheckIcon
                        className="h-6 w-6 text-green-600"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="text-center">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900"
                      >
                        {appointmentToBeEdited.id
                          ? "Edit Appointment"
                          : "Schedule Appointment"}{" "}
                        {isVacant
                          ? " for Vacant Apartment"
                          : " for occupied Apartment"}
                      </Dialog.Title>
                    </div>
                  </div>
                  <div className="flex justify-center border rounded px-1">
                    {/* Schedule Appointment */}
                    <div className="pt-3 w-1/4 text-center sm:pt-5 border-r">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900"
                      >
                        {appointmentToBeEdited.id
                          ? "Edit Appointment"
                          : "Schedule Appointment"}{" "}
                      </Dialog.Title>
                      <div>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DemoContainer components={["DateTimePicker"]}>
                            <div style={{ marginTop: "15px" }}>
                              <DateTimePicker
                                label="Start Date Time"
                                value={
                                  !appointmentToBeEdited.id
                                    ? null
                                    : dayjs(selectedStartTime)
                                }
                                onChange={(
                                  date?:
                                    | string
                                    | number
                                    | Date
                                    | dayjs.Dayjs
                                    | null
                                    | undefined,
                                ) => {
                                  setSelectedStartTime(date);
                                }}
                              />
                            </div>
                          </DemoContainer>
                        </LocalizationProvider>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DemoContainer components={["DateTimePicker"]}>
                            <div style={{ marginTop: "15px" }}>
                              <DateTimePicker
                                label="End Date Time"
                                value={
                                  !appointmentToBeEdited.id
                                    ? null
                                    : dayjs(selectedEndTime)
                                }
                                onChange={(
                                  date?:
                                    | string
                                    | number
                                    | Date
                                    | dayjs.Dayjs
                                    | null
                                    | undefined,
                                ) => {
                                  setSelectedEndTime(date);
                                }}
                              />
                            </div>
                          </DemoContainer>
                        </LocalizationProvider>

                        <div className="flex flex-wrap items-center justify-center my-4">
                          {!appointmentToBeEdited.id && (
                            <div className="text-start md:max-w-[50%] px-3 mb-6 md:mb-0">
                              <label
                                className="text-start text-gray-700 text-sm font-medium mb-2"
                                htmlFor="slotDuration"
                              >
                                Slot Duration
                              </label>
                              <input
                                className="appearance-none block w-full bg-gray-100 text-gray-900 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                                type="number"
                                id="slotDuration"
                                name="slotDuration"
                                onChange={handleSlotChange}
                                disabled={isVacant}
                                value={selectedSlot}
                              />
                            </div>
                          )}
                          {!isVacant && (
                            <div className="text-start md:md:max-w-[50%] px-3 mb-6 md:mb-0">
                              <label
                                className="text-gray-700 text-sm font-medium mb-2"
                                htmlFor="slotCapaity"
                              >
                                Slot Capacity
                              </label>
                              <input
                                className="appearance-none block w-full bg-gray-100 text-gray-900 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                                type="number"
                                id="capacity"
                                name="capacity"
                                value={selectedCapacity}
                                disabled={isVacant}
                                onChange={handleCapacityChange}
                              />
                            </div>
                          )}
                          {isVacant && !appointmentToBeEdited.id && (
                            <div className="mt-4">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Repeat for Next Days
                              </label>
                              <input
                                type="number"
                                min={0}
                                value={numberOfDays}
                                onChange={(e) =>
                                  setNumberOfDays(
                                    Math.max(0, Number(e.target.value)),
                                  )
                                }
                                className="appearance-none block w-full bg-gray-100 text-gray-900 border border-gray-200 rounded py-2 px-3 leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Configue Appointment */}
                    <div className="pt-3 w-[45%] text-center sm:pt-5 border-r">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900"
                      >
                        Marketing Window for Appointment
                      </Dialog.Title>
                      <div>
                        <div className="bg-gray-50 m-2 rounded-lg">
                          <div className="w-full flex items-center justify-center pt-4">
                            <div className="flex items-center bg-gray-100 rounded-full w-full max-w-sm">
                              <button
                                onClick={() => setIsConfigureByDate(true)}
                                className={
                                  "w-1/2 text-xs text-center py-2 rounded-full font-semibold transition duration-200 " +
                                  (isConfigureByDate
                                    ? "bg-blue-600 text-white"
                                    : "text-gray-600")
                                }
                              >
                                Activate by Schedule
                              </button>

                              <button
                                onClick={() => setIsConfigureByDate(false)}
                                className={
                                  "w-1/2 text-xs text-center py-2 rounded-full font-semibold transition duration-200 " +
                                  (!isConfigureByDate
                                    ? "bg-blue-600 text-white"
                                    : "text-gray-600")
                                }
                              >
                                Activate by Lead Time
                              </button>
                            </div>
                          </div>

                          {isConfigureByDate ? (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <div className="m-2 p-2">
                                {/* Improved layout for wider modal */}
                                <div className="flex flex-row items-center gap-4 m-3">
                                  <div className="w-full text-sm">
                                    <DateTimePicker
                                      label="Opening Time"
                                      value={
                                        mainAppointment.openingTime
                                          ? dayjs(mainAppointment.openingTime)
                                          : null
                                      }
                                      onChange={(date) => {
                                        setMainAppointment((prev) => ({
                                          ...prev,
                                          openingTime: date?.toDate() ?? null,
                                        }));
                                      }}
                                      slotProps={{
                                        textField: {
                                          size: "medium",
                                          fullWidth: true,
                                          className: "bg-white rounded-md",
                                        },
                                      }}
                                    />
                                  </div>
                                  <div className="flex items-center justify-center">
                                    <span className="text-gray-400">→</span>
                                  </div>
                                  <div className="w-full">
                                    <DateTimePicker
                                      label="Closing Time"
                                      value={
                                        mainAppointment.closingTime
                                          ? dayjs(mainAppointment.closingTime)
                                          : null
                                      }
                                      onChange={(date) => {
                                        setMainAppointment((prev) => ({
                                          ...prev,
                                          closingTime: date?.toDate() ?? null,
                                        }));
                                      }}
                                      slotProps={{
                                        textField: {
                                          size: "medium",
                                          fullWidth: true,
                                          className: "bg-white rounded-md",
                                        },
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </LocalizationProvider>
                          ) : (
                            <div className="flex justify-between bg-gray-50 m-2 gap-2 p-6 rounded-lg">
                              <div className="relative bg-inherit">
                                <input
                                  type="number"
                                  value={mainAppointment.openHoursBefore}
                                  onChange={(e) => {
                                    setMainAppointment((prev) => ({
                                      ...prev,
                                      openHoursBefore: parseInt(e.target.value),
                                    }));
                                  }}
                                  className="h-12 w-full rounded text-gray-900 px-2 focus:outline-none"
                                  placeholder="0"
                                />
                                <label
                                  htmlFor="hoursBefore"
                                  className="absolute left-0 -top-2 text-xs text-gray-900 bg-inherit mx-1 px-1"
                                >
                                  Hours Before this appt.
                                </label>
                              </div>
                              <div className="relative bg-inherit">
                                <input
                                  type="number"
                                  value={mainAppointment.openHoursAfter}
                                  onChange={(e) => {
                                    setMainAppointment((prev) => ({
                                      ...prev,
                                      openHoursAfter: parseInt(e.target.value),
                                    }));
                                  }}
                                  className="h-12 w-full rounded text-gray-900 px-2 focus:outline-none"
                                  placeholder="0"
                                />
                                <label
                                  htmlFor="username"
                                  className="absolute left-0 -top-2 text-xs text-gray-900 bg-inherit mx-1 px-1"
                                >
                                  Hours After pre appt.
                                </label>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex items-start justify-between bg-gray-50 m-2 p-6 gap-2 rounded-lg">
                          {/* <div className="relative bg-inherit">
                            <input type="number"
                              value={mainAppointment.rosterTime}
                              onChange={(e) => {
                                setMainAppointment((prev) => ({
                                  ...prev,
                                  rosterTime: parseInt(e.target.value)
                                }));
                              }}
                              className="h-12 w-full rounded text-gray-900 px-2 focus:outline-none" placeholder="0" /><label htmlFor="username" className="absolute cursor-text left-0 -top-3 text-xs text-gray-900 bg-inherit mx-1 px-1">Roster Time</label>
                          </div> */}
                          <div className="flex flex-col items-start gap-2">
                            <div className="flex items-start mb-2">
                              <div className="flex items-center h-5">
                                <input
                                  type="checkbox"
                                  checked={mainAppointment.resendUnconfirmed}
                                  onChange={() => {
                                    setMainAppointment((prev) => ({
                                      ...prev,
                                      resendUnconfirmed:
                                        !mainAppointment.resendUnconfirmed,
                                    }));
                                  }}
                                  className="w-4 h-4 border border-gray-300 rounded-sm bg-gray-50 focus:ring-3 focus:ring-blue-300"
                                  required
                                />
                              </div>
                              <label
                                htmlFor="remember"
                                className="ms-2 text-sm font-medium text-gray-900"
                              >
                                Resend to Tenants Who Ignored or Canceled
                              </label>
                            </div>
                            <div className="flex items-start">
                              <div className="flex items-center h-5">
                                <input
                                  type="checkbox"
                                  checked={mainAppointment.resendCancelled}
                                  onChange={() => {
                                    setMainAppointment((prev) => ({
                                      ...prev,
                                      resendCancelled:
                                        !mainAppointment.resendCancelled,
                                    }));
                                  }}
                                  className="w-4 h-4 border border-gray-300 rounded-sm bg-gray-50 focus:ring-3 focus:ring-blue-300"
                                  required
                                />
                              </div>
                              <label
                                htmlFor="remember1"
                                className="ms-2 text-sm font-medium text-gray-900"
                              >
                                Resend Emails for Appointments We Canceled
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className=" flex-col flex w-[30%] items-center justify-start pt-4 ">
                      <div className="flex items-center bg-gray-100 rounded-full gap-2">
                        <button
                          onClick={() => setReminderType("Reminder")}
                          className={
                            " text-xs text-center p-2 rounded-full font-semibold transition duration-200 " +
                            (reminderType == "Reminder"
                              ? "bg-blue-600 text-white"
                              : "text-gray-600")
                          }
                        >
                          Showing reminder
                        </button>

                        <button
                          onClick={() =>
                            setReminderType("Application Reminder")
                          }
                          className={
                            " text-xs text-center p-2  rounded-full font-semibold transition duration-200 " +
                            (reminderType == "Application Reminder"
                              ? "bg-blue-600 text-white"
                              : "text-gray-600")
                          }
                        >
                          Application Reminder
                        </button>
                        <button
                          onClick={() =>
                            setReminderType("Reminder for Details")
                          }
                          className={
                            " text-xs text-center p-2 rounded-full font-semibold transition duration-200 " +
                            (reminderType == "Reminder for Details"
                              ? "bg-blue-600 text-white"
                              : "text-gray-600")
                          }
                        >
                          Details reminder
                        </button>
                      </div>
                      <div className="flex  w-full max-w-sm justify-between p-4 rounded text-white">
                        {reminderType == "Reminder" && (
                          <div className="mt-3  flex flex-col text-center sm:mt-5">
                            <Dialog.Title
                              as="h3"
                              className="text-base font-semibold leading-6 text-gray-900 mb-3"
                            >
                              Showing Reminder
                            </Dialog.Title>
                            <div>
                              <div className="text-start w-full flex-row  p-2">
                                <div className="flex items-center justify-between border-b rounded-lg gap-4 p-2">
                                  <div className="flex flex-wrap w-full items-center justify-between gap-1">
                                    <div className="flex w-full items-center justify-start">
                                      <input
                                        type="number"
                                        value={days !== undefined ? days : ""}
                                        onChange={(e) =>
                                          setDays(
                                            e.target.value
                                              ? Number(e.target.value)
                                              : undefined,
                                          )
                                        }
                                        min={0}
                                        className="text-gray-900 text-center block w-full max-w-[48px] p-1 bg-transparent border-t-transparent border-b-2 border-x-transparent border-b-gray-200 text-xs focus:border-t-transparent focus:border-x-transparent focus:border-b-gray-200 focus:ring-0 disabled:opacity-50 disabled:pointer-events-none"
                                        placeholder="dd"
                                      />
                                      <span>:</span>
                                      <input
                                        type="number"
                                        value={hours !== undefined ? hours : ""}
                                        onChange={(e) =>
                                          setHours(
                                            e.target.value
                                              ? Number(e.target.value)
                                              : undefined,
                                          )
                                        }
                                        max={23}
                                        min={0}
                                        className="text-gray-900 text-center block w-12 p-1 bg-transparent border-t-transparent border-b-2 border-x-transparent border-b-gray-200 text-xs focus:border-t-transparent focus:border-x-transparent focus:border-b-gray-200 focus:ring-0 disabled:opacity-50 disabled:pointer-events-none"
                                        placeholder="hh"
                                      />
                                      <span>:</span>
                                      <input
                                        type="number"
                                        value={
                                          minutes !== undefined ? minutes : ""
                                        }
                                        onChange={(e) =>
                                          setMinutes(
                                            e.target.value
                                              ? Number(e.target.value)
                                              : undefined,
                                          )
                                        }
                                        max={59}
                                        min={0}
                                        className="text-gray-900 text-center block w-12 p-1 bg-transparent border-t-transparent border-b-2 border-x-transparent border-b-gray-200 text-xs focus:border-t-transparent focus:border-x-transparent focus:border-b-gray-200 focus:ring-0 disabled:opacity-50 disabled:pointer-events-none"
                                        placeholder="mm"
                                      />
                                    </div>
                                    <button
                                      className="flex w-full items-center justify-end gap-1 text-blue-800 text-xs font-medium active:translate-y-[1px]"
                                      title="Click on this to add new reminder"
                                      onClick={() => handleAddReminder()}
                                    >
                                      <span className="w-5 font-bold">
                                        <PlusCircleIcon />
                                      </span>
                                      Add New Reminder
                                    </button>
                                  </div>
                                </div>
                                <div className="bg-gray-50 rounded-lg my-2 p-3">
                                  <label className="flex items-center justify-start text-gray-600 text-[13px] font-medium">
                                    Reminders
                                  </label>
                                  <div className="mt-2 flex items-center flex-wrap gap-3">
                                    {reminders &&
                                      reminders
                                        .filter(
                                          (reminder) =>
                                            reminder.reminderType ===
                                            "Reminder",
                                        )
                                        .map((e, i) => {
                                          return (
                                            <div
                                              key={e.id + i}
                                              className="w-max px-3 py-1.5 border flex items-center justify-center gap-2 rounded-full shadow"
                                            >
                                              {e.isReminded && (
                                                <div className="w-2 h-2 rounded-full bg-green-500" />
                                              )}
                                              <span className="text-[10px] text-black  font-medium">
                                                {DislayReminder(e.reminder)}
                                              </span>

                                              <span
                                                onClick={() =>
                                                  handleDeleteReminder(e)
                                                }
                                                className="cursor-pointer flex items-center justify-center"
                                              >
                                                <TrashIcon className="w-3 text-red-600" />
                                              </span>
                                            </div>
                                          );
                                        })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {reminderType == "Application Reminder" && (
                          <div className="mt-3 text-center sm:mt-5">
                            <Dialog.Title
                              as="h3"
                              className="text-base font-semibold leading-6 text-gray-900 mb-3"
                            >
                              Application Reminder
                            </Dialog.Title>
                            <label className="inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={isChecked}
                                onChange={() =>
                                  setApplicationReminderType((prev) =>
                                    prev === "Before" ? "After" : "Before",
                                  )
                                }
                              />
                              <div className="relative w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-blue-600 transition-colors duration-300 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-5 after:w-5 after:transition-transform after:duration-300 peer-checked:after:translate-x-5"></div>
                              <span className="ml-3 text-sm gap-2 font-medium text-gray-900">
                                {applicationReminderType} the showing
                              </span>
                            </label>

                            <div>
                              <div className="text-start w-full p-2">
                                <div className="flex items-center justify-between border-b rounded-lg gap-4 p-2">
                                  <div className="flex flex-wrap w-full items-center justify-between gap-1">
                                    <div className="flex w-full items-center justify-start">
                                      <input
                                        type="number"
                                        value={days !== undefined ? days : ""}
                                        onChange={(e) =>
                                          setDays(
                                            e.target.value
                                              ? Number(e.target.value)
                                              : undefined,
                                          )
                                        }
                                        min={0}
                                        className="text-gray-900 text-center block w-full max-w-[48px] p-1 bg-transparent border-t-transparent border-b-2 border-x-transparent border-b-gray-200 text-xs focus:border-t-transparent focus:border-x-transparent focus:border-b-gray-200 focus:ring-0 disabled:opacity-50 disabled:pointer-events-none"
                                        placeholder="dd"
                                      />
                                      <span>:</span>
                                      <input
                                        type="number"
                                        value={hours !== undefined ? hours : ""}
                                        onChange={(e) =>
                                          setHours(
                                            e.target.value
                                              ? Number(e.target.value)
                                              : undefined,
                                          )
                                        }
                                        max={23}
                                        min={0}
                                        className="text-gray-900 text-center block w-12 p-1 bg-transparent border-t-transparent border-b-2 border-x-transparent border-b-gray-200 text-xs focus:border-t-transparent focus:border-x-transparent focus:border-b-gray-200 focus:ring-0 disabled:opacity-50 disabled:pointer-events-none"
                                        placeholder="hh"
                                      />
                                      <span>:</span>
                                      <input
                                        type="number"
                                        value={
                                          minutes !== undefined ? minutes : ""
                                        }
                                        onChange={(e) =>
                                          setMinutes(
                                            e.target.value
                                              ? Number(e.target.value)
                                              : undefined,
                                          )
                                        }
                                        max={59}
                                        min={0}
                                        className="text-gray-900 text-center block w-12 p-1 bg-transparent border-t-transparent border-b-2 border-x-transparent border-b-gray-200 text-xs focus:border-t-transparent focus:border-x-transparent focus:border-b-gray-200 focus:ring-0 disabled:opacity-50 disabled:pointer-events-none"
                                        placeholder="mm"
                                      />
                                    </div>
                                    <button
                                      className="flex w-full items-center justify-end gap-1 text-blue-800 text-xs font-medium active:translate-y-[1px]"
                                      title="Click on this to add new reminder"
                                      onClick={() => handleAddReminder()}
                                    >
                                      <span className="w-5 font-bold">
                                        <PlusCircleIcon />
                                      </span>
                                      Add New Reminder
                                    </button>
                                  </div>
                                </div>
                                <div className="bg-gray-50 rounded-lg my-2 p-3">
                                  <label className="flex items-center justify-start text-gray-600 text-[13px] font-medium">
                                    Reminders
                                  </label>
                                  <div className="mt-2 flex items-center flex-wrap gap-3">
                                    {reminders &&
                                      reminders
                                        .filter(
                                          (reminder) =>
                                            reminder.reminderType ===
                                            `Application Reminder ${applicationReminderType}`,
                                        )
                                        .map((e, i) => {
                                          return (
                                            <div
                                              key={e.id + i}
                                              className="w-max px-3 py-1.5 border flex items-center justify-center gap-2 rounded-full shadow"
                                            >
                                              {e.isReminded && (
                                                <div className="w-2 h-2 rounded-full bg-green-500" />
                                              )}

                                              <span className="text-[10px] text-black font-medium">
                                                {DislayReminder(e.reminder)}
                                              </span>

                                              <span
                                                onClick={() =>
                                                  handleDeleteReminder(e)
                                                }
                                                className="cursor-pointer flex items-center justify-center"
                                              >
                                                <TrashIcon className="w-3 text-red-600" />
                                              </span>
                                            </div>
                                          );
                                        })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {reminderType == "Reminder for Details" && (
                          <div className="mt-3 text-center sm:mt-5">
                            <Dialog.Title
                              as="h3"
                              className="text-base font-semibold leading-6 text-gray-900 mb-3"
                            >
                              Details Reminder (after showing)
                            </Dialog.Title>
                            <div>
                              <div className="text-start w-full p-2">
                                <div className="flex items-center justify-between border-b rounded-lg gap-4 p-2">
                                  <div className="flex flex-wrap w-full items-center justify-between gap-1">
                                    <div className="flex w-full items-center justify-start">
                                      <input
                                        type="number"
                                        value={days !== undefined ? days : ""}
                                        onChange={(e) =>
                                          setDays(
                                            e.target.value
                                              ? Number(e.target.value)
                                              : undefined,
                                          )
                                        }
                                        min={0}
                                        className="text-gray-900 text-center block w-full max-w-[48px] p-1 bg-transparent border-t-transparent border-b-2 border-x-transparent border-b-gray-200 text-xs focus:border-t-transparent focus:border-x-transparent focus:border-b-gray-200 focus:ring-0 disabled:opacity-50 disabled:pointer-events-none"
                                        placeholder="dd"
                                      />
                                      <span>:</span>
                                      <input
                                        type="number"
                                        value={hours !== undefined ? hours : ""}
                                        onChange={(e) =>
                                          setHours(
                                            e.target.value
                                              ? Number(e.target.value)
                                              : undefined,
                                          )
                                        }
                                        max={23}
                                        min={0}
                                        className="text-gray-900 text-center block w-12 p-1 bg-transparent border-t-transparent border-b-2 border-x-transparent border-b-gray-200 text-xs focus:border-t-transparent focus:border-x-transparent focus:border-b-gray-200 focus:ring-0 disabled:opacity-50 disabled:pointer-events-none"
                                        placeholder="hh"
                                      />
                                      <span>:</span>
                                      <input
                                        type="number"
                                        value={
                                          minutes !== undefined ? minutes : ""
                                        }
                                        onChange={(e) =>
                                          setMinutes(
                                            e.target.value
                                              ? Number(e.target.value)
                                              : undefined,
                                          )
                                        }
                                        max={59}
                                        min={0}
                                        className="text-gray-900 text-center block w-12 p-1 bg-transparent border-t-transparent border-b-2 border-x-transparent border-b-gray-200 text-xs focus:border-t-transparent focus:border-x-transparent focus:border-b-gray-200 focus:ring-0 disabled:opacity-50 disabled:pointer-events-none"
                                        placeholder="mm"
                                      />
                                    </div>
                                    <button
                                      className="flex w-full items-center justify-end gap-1 text-blue-800 text-xs font-medium active:translate-y-[1px]"
                                      title="Click on this to add new reminder"
                                      onClick={() => handleAddReminder()}
                                    >
                                      <span className="w-5 font-bold">
                                        <PlusCircleIcon />
                                      </span>
                                      Add New Reminder
                                    </button>
                                  </div>
                                </div>
                                <div className="bg-gray-50 rounded-lg my-2 p-3">
                                  <label className="flex items-center justify-start text-gray-600 text-[13px] font-medium">
                                    Reminders
                                  </label>
                                  <div className="mt-2  flex items-center flex-wrap gap-3">
                                    {reminders &&
                                      reminders
                                        .filter(
                                          (reminder) =>
                                            reminder.reminderType ===
                                            "Reminder for Details",
                                        )
                                        .map((e, i) => {
                                          return (
                                            <div
                                              key={e.id + i}
                                              className="w-max px-3 py-1.5 border flex items-center justify-center gap-2 rounded-full shadow"
                                            >
                                              {e.isReminded && (
                                                <div className="w-2 h-2 rounded-full bg-green-500" />
                                              )}
                                              <span className="text-[10px] text-black font-medium">
                                                {DislayReminder(e.reminder)}
                                              </span>

                                              <span
                                                onClick={() =>
                                                  handleDeleteReminder(e)
                                                }
                                                className="cursor-pointer flex items-center justify-center"
                                              >
                                                <TrashIcon className="w-3 text-red-600" />
                                              </span>
                                            </div>
                                          );
                                        })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap md:flex-nowrap w-full mt-5 items-center justify-center gap-3">
                    <button
                      type="button"
                      className="flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                      onClick={() => {
                        setOpen(false);
                        setMainAppointment(emptyMainAppointment);
                      }}
                      ref={cancelButtonRef}
                    >
                      Cancel
                    </button>

                    {appointmentToBeEdited.id && (
                      <button
                        disabled={
                          selectedStartTime === null || selectedEndTime === null
                        }
                        type="button"
                        className="flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:col-start-2"
                        onClick={() => DeleteAppointment()}
                      >
                        Delete Appointment
                      </button>
                    )}
                    <button
                      disabled={
                        selectedStartTime === null || selectedEndTime === null
                      }
                      type="button"
                      className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2"
                      onClick={handleOnSave}
                    >
                      {appointmentToBeEdited.id
                        ? "Save Appointment"
                        : "Generate Appointments"}
                    </button>
                  </div>
                  {loading && (
                    <div className="absolute bg-gray-300/80 top-0 left-0 w-full h-full flex items-center justify-center">
                      <Loading />
                    </div>
                  )}
                  <MessageBox
                    title="Confirm Deletion"
                    message="Are you sure you want to delete the selected reminder? This action cannot be undone. Click OK to confirm."
                    isOpen={confirmDeleteReminder}
                    setIsOpen={setConfirmDeleteReminder}
                    okButtonText="Ok"
                    cancelButtonText="Cancel"
                    okButtonFunction={handleDeleteReminderOnConfirm}
                    cancelButtonFunction={() => setConfirmDeleteReminder(false)}
                    okButtonStyle={{
                      backgroundColor: "rgb(239 68 68)",
                    }}
                    iconColor={{ color: "rgb(239 68 68)" }}
                  />
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      <Notification
        isOpen={openNotification}
        message={notificationMessage}
        messageType={notificationMessageType}
        setIsOpen={setOpenNotification}
      />
    </div>
  );
}
